import {
  ICartEntry,
  IComboSlot,
  IItem,
  ISection,
  MenuObject,
  PickerSelection,
} from '@rbi-ctg/menu';
import { IModifierSelectionAction } from 'utils/wizard';

import { ServiceMode } from '../service-mode/types';

export enum ProductWizardStep {
  ProductHome,
  ProductCustomization,
}

export type ProductMenuObject = Exclude<MenuObject, ISection>;

export type UserSelectionPickerAspect = { [id: string]: string };

export interface IUserSelectionComboSlotData {
  comboSlot: IComboSlot | null;
  selectedItem: IItem | null;
}
export type UserSelectionComboSlot = Map<string, IUserSelectionComboSlotData>;

export type IModifierSelectionActionWithData = IModifierSelectionAction & {
  isSanityDefaultSelection: boolean;
};

export type UserSelectionModifierMap = {
  [modifierOptionKey: string]: IModifierSelectionActionWithData;
};

export type UserSelectionModifierSlotMap = {
  [modifierKey: string]: UserSelectionModifierMap;
};

export type UserSelectionModifier = {
  [slotKey: string]: UserSelectionModifierSlotMap;
};

export interface IUserSelection {
  pickerAspects?: UserSelectionPickerAspect;
  comboSlot?: UserSelectionComboSlot;
  modifiers: UserSelectionModifier;
}

/**
 * Tracks if the user made any modifications on the selection options.
 */
export interface IUserManualModifications {
  pickerAspects: boolean;
  comboSlot: boolean;
  modifiers: boolean;
}

export type Product = PickerSelection;

export type ProductOption = IItem | IComboSlot;

export type ProductToCustomize = { product: IItem; slotKey: string };

export type SubmitCustomizationsFn = (args: {
  modifiers: UserSelectionModifierSlotMap;
  selectedItem: IItem;
  slotKey: string;
}) => void;

export interface IProductWizardContext {
  /**
   * Current step in the flow
   */
  currentStep: ProductWizardStep;
  /**
   * Navigates to the product customization screen
   */
  goToCustomizeProduct: (slotKey: string) => void;
  /**
   * Navigates to the wizard home page
   */
  goToHome: () => void;
  /**
   * The item to customize on the customization screen
   */
  itemToCustomize: IItem | null;
  /**
   * The raw menu data object passed to wizard
   */
  menuObject: ProductMenuObject;
  /**
   * The order quantity for the selected product
   */
  productQuantity: number;
  /**
   * The selected Combo/Item that can be customized and added to cart
   */
  selectedProduct: Product;
  /**
   * Sets the order quantity for the selected product
   */
  setProductQuantity: React.Dispatch<React.SetStateAction<number>>;
  /**
   * The slot key to be customized from combo slot selections
   */
  slotKeyToCustomize: string;
  /**
   * Method to submit customizations
   */
  submitCustomizations: SubmitCustomizationsFn;
  /**
   * The user's current selections (pickerAspects, comboSlots, modifiers)
   */
  userSelections: IUserSelection;
  /**
   * Tracks if the user has made any modifications to each selection (pickerAspects, comboSlots, modifiers)
   */
  manualModifications: IUserManualModifications;
  /**
   * Method to update picker aspect selection
   */
  updatePickerAspectSelections: (args: UserSelectionPickerAspect) => void;
  /**
   * Indicates if there have been any user modifications to any combo item
   */
  isDirty: boolean;
}

export interface IProductWizardProvider {
  product: ProductMenuObject;
}

export type SetDefaultSelectionsFn = (args: {
  menuObject: ProductMenuObject;
  pickerSelectionOverrides?: Record<string, string>;
  cartEntryOverrides?: ICartEntry;
  serviceMode: ServiceMode | null;
}) => IUserSelection;
